/* ---------------------------------------------------------------------------
   Working calendars — which days a contract's works are counted on (LC-43).

   A duration in a programme is WORKING days. Until this module the register
   assumed every day was one, because the vendored solver does (its
   `AddWorkingDays(d, n)` is `d + n`), and a ten-day pour planned over Eid came
   out finishing two days before it could. A calendar says which days count:

     · a working week, Monday first — Mon–Sat is `1111110`;
     · public holidays, one day each;
     · seasonal windows in which no work is planned: the monsoon, the snowmelt,
       the high river.

   Kept per department by the administrator (`calendar.manage`), and chosen by
   the planner for a contract when the baseline is taken. The solver in
   ./programme.ts and the forecast in ./forecast.ts both count through here and
   nowhere else.

   PURE, UTC, AND `YYYY-MM-DD` THROUGHOUT, the rules ./periods.ts keeps and for
   the same reasons.

   ══ WORKING-DAY INDEX ══

   The solver works in working-day offsets, exactly as the vendored one does in
   calendar days: day 0 is the first working day on or after the project start,
   day 1 the next working day, and so on. `CalendarIndex` turns an offset into a
   date and back. Doing the CPM in that space and converting at the edges keeps
   the passes the same arithmetic as the vendored `Recompute()`, and a
   continuous calendar makes the two identical, which `PG-02` still checks.
   --------------------------------------------------------------------------- */

import { addDays, isIsoDay } from './periods.ts';

/** One public holiday. */
export interface CalendarHoliday {
  on: string;
  name: string;
}

/** A seasonal window with no work planned, both ends inclusive. */
export interface CalendarWindow {
  from: string;
  to: string;
  name: string;
}

export interface WorkCalendar {
  id: string;
  name: string;
  /** Seven characters, Monday first: `1` a working day, `0` a rest day. */
  week: string;
  holidays: CalendarHoliday[];
  windows: CalendarWindow[];
  /** Seeded, not yet confirmed by the department. */
  provisional?: boolean;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

/** Every day a working day — what the vendored solver assumes, and old baselines were solved on. */
export const EVERY_DAY: WorkCalendar = {
  id: 'every-day',
  name: 'Every day',
  week: '1111111',
  holidays: [],
  windows: [],
};

export const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** Whether a week string is seven 0/1 characters with at least one working day. */
export function isWeek(week: string): boolean {
  return /^[01]{7}$/.test(week) && week.includes('1');
}

/** The week as words: "Mon–Sat", "Sun–Fri", or the days listed. */
export function weekLabel(week: string): string {
  if (!isWeek(week)) return 'no working days';
  if (week === '1111111') return 'Every day';
  const on = [...week].map((c) => c === '1');
  /* One run of working days, possibly wrapping past Sunday, reads as a range. */
  const rest = on.map((w, i) => (w ? -1 : i)).filter((i) => i >= 0);
  if (rest.length > 0) {
    const firstWork = (rest[rest.length - 1]! + 1) % 7;
    let run = 0;
    for (let k = 0; k < 7 && on[(firstWork + k) % 7]; k += 1) run += 1;
    if (run === on.filter(Boolean).length) {
      const last = (firstWork + run - 1) % 7;
      return run === 1 ? WEEKDAY_NAMES[firstWork]! : `${WEEKDAY_NAMES[firstWork]}–${WEEKDAY_NAMES[last]}`;
    }
  }
  return WEEKDAY_NAMES.filter((_, i) => on[i]).join(', ');
}

/** Monday 0 … Sunday 6, for a UTC calendar day. */
function weekday(iso: string): number {
  return (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;
}

/** Whether work is planned on a day. */
export function isWorkingDay(cal: WorkCalendar, iso: string): boolean {
  if (cal.week[weekday(iso)] !== '1') return false;
  if (cal.holidays.some((h) => h.on === iso)) return false;
  return !cal.windows.some((w) => w.from <= iso && iso <= w.to);
}

/** How far any walk here goes before deciding a calendar has no working days left. */
const HORIZON_DAYS = 3660 * 3;

/** The day itself if it is a working day, else the next one. */
export function nextWorkingDay(cal: WorkCalendar, iso: string): string {
  let d = iso;
  for (let i = 0; i < HORIZON_DAYS; i += 1) {
    if (isWorkingDay(cal, d)) return d;
    d = addDays(d, 1);
  }
  return iso;
}

/**
 * The last working day of `n` working days starting on `start` (itself moved to
 * a working day). `n` of zero or one both finish on the start day — a
 * milestone, or a day's work.
 */
export function finishAfter(cal: WorkCalendar, start: string, n: number): string {
  const index = new CalendarIndex(cal, start);
  return index.dateAt(Math.max(0, Math.round(n) - 1));
}

/** Working days from `from` up to but not including `to`. Negative where `to` is earlier. */
export function workingDaysBetween(cal: WorkCalendar, from: string, to: string): number {
  return new CalendarIndex(cal, from).indexOf(to);
}

/**
 * Working-day offsets from a project start, and back.
 *
 * Offset 0 is the first working day on or after `start`. The list of working
 * days is extended as far as a question needs, so a programme of four years
 * costs four years of days once rather than a walk per question.
 */
export class CalendarIndex {
  public readonly calendar: WorkCalendar;
  public readonly start: string;
  private readonly days: string[] = [];
  private cursor: string;

  constructor(calendar: WorkCalendar, start: string) {
    this.calendar = calendar;
    this.start = start;
    this.cursor = start;
  }

  private extendTo(i: number): boolean {
    let guard = 0;
    while (this.days.length <= i && guard < HORIZON_DAYS) {
      if (isWorkingDay(this.calendar, this.cursor)) this.days.push(this.cursor);
      this.cursor = addDays(this.cursor, 1);
      guard += 1;
    }
    return this.days.length > i;
  }

  /** The working day at an offset. Negative offsets walk back before the start. */
  dateAt(i: number): string {
    const n = Math.round(i);
    if (n < 0) {
      let d = this.start;
      let left = -n;
      for (let k = 0; k < HORIZON_DAYS && left > 0; k += 1) {
        d = addDays(d, -1);
        if (isWorkingDay(this.calendar, d)) left -= 1;
      }
      return d;
    }
    return this.extendTo(n) ? this.days[n]! : this.cursor;
  }

  /**
   * The offset of a day: how many working days fall from the start up to it,
   * not counting it. A rest day takes the offset of the working day after it.
   */
  indexOf(iso: string): number {
    if (!isIsoDay(iso)) return 0;
    if (iso < this.start) {
      let count = 0;
      let d = iso;
      for (let k = 0; k < HORIZON_DAYS && d < this.start; k += 1) {
        if (isWorkingDay(this.calendar, d)) count += 1;
        d = addDays(d, 1);
      }
      return -count;
    }
    while ((this.days.length === 0 || this.days[this.days.length - 1]! < iso) && this.cursor <= iso) {
      if (!this.extendTo(this.days.length)) break;
    }
    let lo = 0;
    let hi = this.days.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.days[mid]! < iso) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }
}

/* --------------------------- reading one off the wire --------------------------- */

/**
 * A calendar as posted, checked — or the sentence saying what is wrong with it.
 * Shared by the admin's form and functions/lib/calendars.ts, so both refuse the
 * same things.
 */
export function checkCalendar(c: {
  name: string;
  week: string;
  holidays: readonly CalendarHoliday[];
  windows: readonly CalendarWindow[];
}): string | null {
  if (c.name.trim() === '') return 'A calendar needs a name.';
  if (!isWeek(c.week)) return 'A working week needs at least one working day.';
  for (const h of c.holidays) {
    if (!isIsoDay(h.on)) return `Holiday "${h.name}" needs a date, as YYYY-MM-DD.`;
  }
  for (const w of c.windows) {
    if (!isIsoDay(w.from) || !isIsoDay(w.to)) return `Window "${w.name}" needs both dates, as YYYY-MM-DD.`;
    if (w.to < w.from) return `Window "${w.name}" ends before it starts.`;
  }
  if (c.holidays.length > 400) return 'That is more holidays than a calendar holds.';
  if (c.windows.length > 60) return 'That is more seasonal windows than a calendar holds.';
  return null;
}

/**
 * Lines of text to holidays or windows, for the admin's form: one per line,
 * `2026-12-25 Christmas` or `2026-07-01 2026-08-31 Monsoon`. Lines that do not
 * parse come back in `bad`.
 */
export function parseCalendarLines(text: string): { holidays: CalendarHoliday[]; windows: CalendarWindow[]; bad: string[] } {
  const holidays: CalendarHoliday[] = [];
  const windows: CalendarWindow[] = [];
  const bad: string[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (line === '') continue;
    const range = /^(\d{4}-\d{2}-\d{2})\s*(?:→|->|to|–|-)?\s*(\d{4}-\d{2}-\d{2})\s*(.*)$/.exec(line);
    if (range && isIsoDay(range[1]!) && isIsoDay(range[2]!)) {
      windows.push({ from: range[1]!, to: range[2]!, name: range[3]!.trim() || 'Non-working' });
      continue;
    }
    const day = /^(\d{4}-\d{2}-\d{2})\s*(.*)$/.exec(line);
    if (day && isIsoDay(day[1]!)) {
      holidays.push({ on: day[1]!, name: day[2]!.trim() || 'Holiday' });
      continue;
    }
    bad.push(line);
  }
  return { holidays, windows, bad };
}

/** The inverse of `parseCalendarLines`, for filling the form. */
export function calendarLines(c: Pick<WorkCalendar, 'holidays' | 'windows'>): { holidays: string; windows: string } {
  return {
    holidays: c.holidays.map((h) => `${h.on} ${h.name}`).join('\n'),
    windows: c.windows.map((w) => `${w.from} ${w.to} ${w.name}`).join('\n'),
  };
}
